import { ReactComponent as MainLogoSvg } from 'assets/svg/Main-Logo.svg';

import { ReactComponent as FeatureBedroomSvg } from 'assets/svg/feature-bedroom.svg';
import { ReactComponent as FeatureBathroomSvg } from 'assets/svg/feature-bathroom.svg';
import { ReactComponent as FeatureBuildAreaSvg } from 'assets/svg/feature-buildarea.svg';
import { ReactComponent as FeaturePlotSizeSvg } from 'assets/svg/feature-plotsize.svg';
import { ReactComponent as DefaultPropertyImageSvg } from 'assets/svg/Default-Property-Image.svg';

import PlaceIcon from '@mui/icons-material/Place';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import EuroIcon from '@mui/icons-material/Euro';
import HomeIcon from '@mui/icons-material/Home';
import SquareFootIcon from '@mui/icons-material/SquareFoot';
import BedIcon from '@mui/icons-material/Bed';
import BathtubIcon from '@mui/icons-material/Bathtub';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import ReactCountryFlag from 'react-country-flag';

import { formatNumber, convertDateIntoLocaleString, GetCountryCode } from 'util/Funcs';

import { ShareLinkDto } from 'types/SharableLink';
import { PropertyDTO } from 'util/Types';
import { ReactComponent as DotSvg } from 'assets/svg/address-dot.svg';
import { Divider } from '@mui/material';
import getSymbolFromCurrency from 'currency-symbol-map';
import styled from 'styled-components';
import { useEffect, useState } from 'react';
import { PropertyPagination } from 'components/Pagination';
import { useModals } from 'pages/MainProvider';
import useSharableLinkStore from 'store/sharableLinksStore';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

const MIN_W = 400;
const MIN_H = 200;

// Hook for validating images
const useImageValidation = (imageUrls: string[] | undefined) => {
    const [validImages, setValidImages] = useState<string[]>([]);
    const [isValidating, setIsValidating] = useState(true);

    useEffect(() => {
        if (!imageUrls || imageUrls.length === 0) {
            setValidImages([]);
            setIsValidating(false);
            return;
        }

        const validateImages = async () => {
            setIsValidating(true);
            const valid: string[] = [];

            for (const url of imageUrls) {
                try {
                    const img = new Image();
                    const isValid = await new Promise<boolean>((resolve) => {
                        img.onload = () => resolve(img.width >= MIN_W && img.height >= MIN_H);
                        img.onerror = () => resolve(false);
                        img.src = url;
                    });

                    if (isValid) {
                        valid.push(url);
                    }
                } catch {
                    // Skip invalid images
                }
            }

            setValidImages(valid);
            setIsValidating(false);
        };

        validateImages();
    }, [imageUrls]);

    return { validImages, isValidating };
};

// Property Grid Image Component
const PropertyGridImage = ({ property }: { property: PropertyDTO }) => {
    const { validImages, isValidating } = useImageValidation(property.imageUrls);

    return (
        <div className="relative">
            {isValidating ? (
                <div className="w-full h-[200px] bg-gray-100 animate-pulse"></div>
            ) : validImages.length > 0 ? (
                <img src={validImages[0]} className="w-full h-[200px] object-cover" alt={property.title} loading="lazy" />
            ) : (
                <div className="w-full h-[200px] bg-gray-100 flex items-center justify-center">
                    <img src={'/image/DefaultImageForShareProperty.png'} className="w-full h-full object-cover" alt={property.title} />
                </div>
            )}
        </div>
    );
};

const PropertyCard = ({
    item,
    onPropertyClick,
    shareableLinkId,
    shareableLinkProperty,
}: {
    item: PropertyDTO;
    onPropertyClick: (property: PropertyDTO) => void;
    shareableLinkId: string;
    shareableLinkProperty: any;
}) => {
    const details = item.details;
    const queryClient = useQueryClient();
    const { rateProperty, removePropertyRating } = useSharableLinkStore();

    // Local state for optimistic updates
    const [optimisticRating, setOptimisticRating] = useState<'like' | 'dislike' | null>(shareableLinkProperty?.rating || null);

    // Update local state when prop changes
    useEffect(() => {
        setOptimisticRating(shareableLinkProperty?.rating || null);
    }, [shareableLinkProperty?.rating]);

    // Use optimistic rating for immediate UI updates
    const currentRating = optimisticRating;

    // Debug logging
    console.log('🎯 PropertyCard render - shareableLinkProperty:', shareableLinkProperty);
    console.log('🎯 Current rating being used:', currentRating);
    console.log('🎯 Optimistic rating state:', optimisticRating);

    // Mutation for rating property
    const rateMutation = useMutation({
        mutationFn: ({ rating }: { rating: 'like' | 'dislike' }) => rateProperty(shareableLinkProperty?.id, rating),
        onMutate: ({ rating }) => {
            // Update local state immediately for instant UI feedback
            console.log('🔄 Setting optimistic rating to:', rating);
            setOptimisticRating(rating);
        },
        onError: () => {
            // Revert to original rating on error
            setOptimisticRating(shareableLinkProperty?.rating || null);
        },
        onSuccess: () => {
            // Optionally refetch data to ensure consistency
            queryClient.invalidateQueries({ queryKey: ['loadSharableLinks', shareableLinkId] });
        },
    });

    // Mutation for removing rating
    const removeRatingMutation = useMutation({
        mutationFn: () => removePropertyRating(shareableLinkProperty?.id),
        onMutate: () => {
            // Update local state immediately for instant UI feedback
            console.log('🔄 Setting optimistic rating to: null');
            setOptimisticRating(null);
        },
        onError: () => {
            // Revert to original rating on error
            setOptimisticRating(shareableLinkProperty?.rating || null);
        },
        onSuccess: () => {
            // Optionally refetch data to ensure consistency
            queryClient.invalidateQueries({ queryKey: ['loadSharableLinks', shareableLinkId] });
        },
    });

    const handleRating = (e: React.MouseEvent, rating: 'like' | 'dislike') => {
        e.stopPropagation(); // Prevent opening property detail modal

        // Log the API call details for debugging
        console.log(`${currentRating === rating ? 'Removing' : 'Setting'} rating for shareableLinkPropertyId:`, shareableLinkProperty?.id);
        console.log('Current rating:', currentRating, 'New rating:', rating);
        console.log('ShareableLinkProperty object:', shareableLinkProperty);

        if (!shareableLinkProperty?.id) {
            console.error('Cannot rate property: shareableLinkPropertyId is missing');
            return;
        }

        if (currentRating === rating) {
            // If clicking the same rating, remove it
            console.log('Removing rating via DELETE /shareable-links/property-rating/' + shareableLinkProperty.id);
            removeRatingMutation.mutate();
        } else {
            // Otherwise set new rating
            console.log('Setting rating via PATCH /shareable-links/property-rating with payload:', {
                shareableLinkPropertyId: shareableLinkProperty.id,
                rating,
            });
            rateMutation.mutate({ rating });
        }
    };

    return (
        <div
            className="transition-all duration-200 ease-in-out hover:shadow-md cursor-pointer"
            style={{
                willChange: 'transform',
                transform: 'translateZ(0)', // Hardware acceleration
            }}
            onClick={() => onPropertyClick(item)}
        >
            <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
                {/* Image Section */}
                <div className="relative">
                    <PropertyGridImage property={item} />

                    {/* Like/Dislike Buttons */}
                    <div className="absolute top-3 right-3 flex items-center gap-2">
                        <button
                            onClick={(e) => handleRating(e, 'like')}
                            className={`p-2 rounded-full transition-all duration-200 ${
                                currentRating === 'like' ? 'bg-green-500 text-white shadow-lg' : 'bg-white/80 hover:bg-white text-gray-600 hover:text-green-500'
                            } ${rateMutation.isPending || removeRatingMutation.isPending ? 'opacity-50 cursor-not-allowed' : ''}`}
                            disabled={rateMutation.isPending || removeRatingMutation.isPending}
                            title={currentRating === 'like' ? 'Remove like' : 'Like this property'}
                        >
                            <ThumbUpIcon className="w-4 h-4" />
                        </button>
                        <button
                            onClick={(e) => handleRating(e, 'dislike')}
                            className={`p-2 rounded-full transition-all duration-200 ${
                                currentRating === 'dislike' ? 'bg-red-500 text-white shadow-lg' : 'bg-white/80 hover:bg-white text-gray-600 hover:text-red-500'
                            } ${rateMutation.isPending || removeRatingMutation.isPending ? 'opacity-50 cursor-not-allowed' : ''}`}
                            disabled={rateMutation.isPending || removeRatingMutation.isPending}
                            title={currentRating === 'dislike' ? 'Remove dislike' : 'Dislike this property'}
                        >
                            <ThumbDownIcon className="w-4 h-4" />
                        </button>
                    </div>
                </div>

                {/* Content Section */}
                <div className="p-4">
                    <div className="h-[48px] mb-4">
                        <h3 className="text-lg font-semibold line-clamp-2 flex items-center">
                            <span
                                className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-medium mr-2 flex-shrink-0 ${
                                    item.details.listingType === 'rental' ? 'bg-green-100 text-green-600' : 'bg-blue-100 text-blue-600'
                                }`}
                            >
                                {item.details.listingType === 'rental' ? 'R' : 'S'}
                            </span>
                            <span className="line-clamp-2">{item.title}</span>
                        </h3>
                    </div>

                    <div className="grid grid-cols-2 gap-4 mb-4">
                        <div>
                            <div className="flex items-center gap-2 mb-1">
                                <EuroIcon className="text-gray-500 w-5 h-5" />
                                <div className="font-medium">
                                    {item.details.price && item.details.price > 0 ? `€ ${formatNumber(item.details.price)}` : 'On Request'}
                                </div>
                            </div>
                        </div>
                        <div>
                            <div className="flex items-center gap-2 mb-1">
                                {item.address.Country && (
                                    <ReactCountryFlag
                                        svg
                                        countryCode={GetCountryCode(item.address.Country || '') || ''}
                                        style={{ width: '1em', height: '1em' }}
                                        className="w-4 h-4"
                                    />
                                )}
                                <div className="font-medium ml-2">{item.address.City}</div>
                            </div>
                        </div>
                        <div>
                            <div className="flex items-center gap-2 mb-1">
                                <HomeIcon className="text-gray-500 w-5 h-5" />
                                <div className="font-medium">{item.details.propertyType || ''}</div>
                            </div>
                        </div>
                        <div>
                            <div className="flex items-center gap-2 mb-1">
                                {item.details.sizePlot ? (
                                    <>
                                        <SquareFootIcon className="text-gray-500 w-5 h-5" />
                                        <div className="font-medium">{`${formatNumber(item.details.sizePlot)} m²`}</div>
                                    </>
                                ) : (
                                    <>
                                        <HomeIcon className="text-gray-500 w-5 h-5" />
                                        <div className="font-medium">{`${formatNumber(item.details.sizeLiving || 0)} m²`}</div>
                                    </>
                                )}
                            </div>
                        </div>
                        <div>
                            <div className="flex items-center gap-2 mb-1">
                                <BedIcon className="text-gray-500 w-5 h-5" />
                                <div className="font-medium">{item.details.bedrooms || '0'}</div>
                            </div>
                        </div>
                        <div>
                            <div className="flex items-center gap-2 mb-1">
                                <BathtubIcon className="text-gray-500 w-5 h-5" />
                                <div className="font-medium">{item.details.bathrooms || '0'}</div>
                            </div>
                        </div>
                    </div>

                    <div className="text-sm text-gray-500">{convertDateIntoLocaleString(item.createdAt)}</div>
                </div>
            </div>
        </div>
    );
};

const ContactContainer = styled('div')({
    padding: '16px',
    border: '1px solid #DADADA',
    background: 'white',
    borderRadius: '10px',
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
});

const ViewPropertyInLinkSection = ({ linkitem }: { linkitem?: ShareLinkDto }) => {
    if (!linkitem) return null;

    // Log the shareable link object to understand the structure
    console.log('Shareable Link Object:', linkitem);
    console.log('Shareable Link Properties:', linkitem.shareableLinkProperties);
    console.log('Properties:', linkitem.properties);

    const countPerPage = 5;
    const [page, setPage] = useState<number>(0);
    const maxPage = Math.round(linkitem.properties.length / countPerPage);

    const currentProperties: PropertyDTO[] = linkitem.properties.slice(page * countPerPage, page * countPerPage + countPerPage);

    const contact = linkitem.contactInformation;
    const { showPropertyDetailModal } = useModals();

    const onPropertyClickd = (property: PropertyDTO) => {
        showPropertyDetailModal(property, true, undefined, true);
    };

    const onHandlePageChange = (p: number) => {
        setPage(p);
    };

    return (
        <div>
            {/* icon */}
            <div className="bg-white h-[72px] flex items-center justify-center">
                <img src="/image/HelloHereIcon.png" className="mx-auto" />
            </div>

            {/* title */}
            <div
                className="h-[190px] bg-my-blue flex flex-col items-center justify-center text-white text-center"
                style={{
                    background: 'url(/image/ViewShareLinkHeaderBackground.png)',
                    backgroundColor: 'rgb(49 118 188)',
                }}
            >
                <div className="text-[20px] font-semibold">{linkitem.title}</div>
                <div className="pt-[5px]">{linkitem.customMessage}</div>
            </div>

            {/* properties */}
            <div className="py-[50px] md:px-[32px] px-[12px]">
                <div className="mx-auto max-w-[1200px]">
                    <div className="text-my-blue text-[20px] font-semibold">{`${linkitem.properties.length} Property Showcase`}</div>

                    <div className="grid grid-cols-1 [@media(min-width:600px)]:grid-cols-2 [@media(min-width:980px)]:grid-cols-3 gap-4 pt-[24px]">
                        {currentProperties.map((p, index) => {
                            // Find the corresponding shareableLinkProperty entry for this property
                            // The shareableLinkProperties uses 'listingId' field to match with property
                            const shareableLinkProperty = linkitem.shareableLinkProperties?.find((slp: any) => slp.listingId === p.propertyId);

                            // Log the mapping for debugging
                            console.log(`=== Property Mapping Debug ===`);
                            console.log(`Property object:`, p);
                            console.log(`Property ID fields - propertyId: ${p.propertyId}, entityId: ${p.details?.entityId}, webPageId: ${p.webPageId}`);
                            console.log('Available shareableLinkProperties:', linkitem.shareableLinkProperties);
                            console.log(`Attempting to match against listingId field`);
                            console.log(`Found matching shareableLinkProperty:`, shareableLinkProperty);
                            console.log(`Using shareableLinkPropertyId: ${shareableLinkProperty?.id}`);

                            if (!shareableLinkProperty?.id) {
                                console.warn(`❌ No shareableLinkPropertyId found for property ${p.propertyId}`);
                                console.warn(
                                    `Available listingIds:`,
                                    linkitem.shareableLinkProperties?.map((slp: any) => slp.listingId),
                                );
                                console.warn(`Trying different property fields:`);
                                console.warn(`- propertyId: ${p.propertyId}`);
                                console.warn(`- entityId: ${p.details?.entityId}`);
                                console.warn(`- webPageId: ${p.webPageId}`);
                            } else {
                                console.log(`✅ Successfully found shareableLinkPropertyId: ${shareableLinkProperty?.id}`);
                            }

                            return (
                                <PropertyCard
                                    key={index}
                                    item={p}
                                    onPropertyClick={onPropertyClickd}
                                    shareableLinkId={linkitem.id}
                                    shareableLinkProperty={shareableLinkProperty}
                                />
                            );
                        })}
                    </div>
                </div>
            </div>

            <div className="flex items-center justify-center">
                <PropertyPagination currentPage={page} totalPageCount={maxPage} handlePageChange={onHandlePageChange} />
            </div>
            {/* contact */}
            {contact && (
                <div className="mx-auto max-w-[1200px] flex flex-col gap-[36px] pb-[50px]  md:px-[32px] px-[12px]">
                    <div className="text-center">
                        <div className="text-my-blue">{`Get in Touch`}</div>
                        <div className="font-semibold text-[24px]">{contact.companyName}</div>
                    </div>
                    <div className="grid md:grid-cols-3 grid-cols-1 gap-[18px]">
                        <ContactContainer className="shadow-hello-gray-week">
                            <div className="rounded-[4px] w-[42px] h-[42px] border border-my-green bg-my-green bg-opacity-10 flex items-center justify-center">
                                <PlaceIcon className="!text-my-green" />
                            </div>

                            <div className="text-[16px] font-semibold">{`Address`}</div>
                            <div>{contact.officeAddress}</div>
                        </ContactContainer>

                        <ContactContainer className="shadow-hello-gray-week">
                            <div className="rounded-[4px] w-[42px] h-[42px] border border-my-green bg-my-green bg-opacity-10 flex items-center justify-center">
                                <EmailIcon className="!text-my-green" />
                            </div>

                            <div className="text-[16px] font-semibold">{`Email`}</div>
                            <div>{contact.emailAddress}</div>
                        </ContactContainer>

                        <ContactContainer className="shadow-hello-gray-week">
                            <div className="rounded-[4px] w-[42px] h-[42px] border border-my-green bg-my-green bg-opacity-10 flex items-center justify-center">
                                <PhoneIcon className="!text-my-green" />
                            </div>

                            <div className="text-[16px] font-semibold">{`Phone`}</div>
                            <div>{contact.phoneNumber}</div>
                        </ContactContainer>
                    </div>
                </div>
            )}

            {/* footer */}
            <div className="md:h-[55px] h-[50px] text-center flex items-center justify-center bg-white md:text-base text-[10px]">
                <span className="mr-[4px]">{`This property Share link was generated using `}</span>
                <span className="cursor-pointer flex items-center">
                    <a
                        href={`https://${window.location.hostname}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-medium hover:text-my-blue transition-colors"
                    >{`hello:data`}</a>
                    <img src="/image/location.png" className="m:h-[21px] h-[14px]" />
                </span>
            </div>
        </div>
    );
};

export default ViewPropertyInLinkSection;
